import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/molecules/Sheet";
import Button from "@/components/atoms/Button";
import { SquarePen, X } from "lucide-react";
import { UseFormReturn } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/atoms/Form";
import type FormFieldItem from "@/types/Form/FormFieldItem";
import { Switch } from "@/components/atoms/Switch";
import useAccess from "@/hooks/useAccess";
import { useState } from "react";

export type MODE = "edit" | "create";
export type OnSubmitProps<T> = (
  mode: MODE,
  data: any,
  sheetData: T | null,
  e: any
) => void;

type Props<T> = {
  title?: string;
  mode?: MODE;
  form: UseFormReturn<any, any, any>;
  open: boolean;
  edit?: boolean;
  onOpen: (open: boolean) => void;
  data: T | null;
  formFields: FormFieldItem<T>[];
  setEdit?: (edit: boolean) => void;
  setData?: (data: T | null) => void;
  onSubmit: OnSubmitProps<T>;
  side?: "left" | "right" | "bottom" | "top";
  loading?: boolean;
  baseError?: string | null;
  showSwitch?: boolean;
  onUnfreezeSowChange?: (value: boolean) => void;
  formClass?: string;
};

const TabSheet = <T extends unknown>({
  title,
  mode = "edit",
  form,
  formFields,
  edit = true,
  data,
  open,
  onOpen,
  setData = () => {},
  setEdit = () => {},
  onSubmit,
  side = "right",
  loading = false,
  baseError = null,
  showSwitch = false,
  onUnfreezeSowChange = () => {},
  formClass = "space-y-8",
}: Props<T>) => {
  const Submit = (d: any, e: any) => {
    e.preventDefault();
    if (d) onSubmit(mode, d, data, e);
  };

  const { getAccess } = useAccess();
  const [unfreezeSow, setUnfreezeSow] = useState<boolean>(false);

  const handleUnfreezeSow = (isChecked: boolean) => {
    onUnfreezeSowChange(isChecked);
    setUnfreezeSow(isChecked);
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(opened) => {
        onOpen(opened);
        if (!opened) {
          setData(null);
          form.reset();
          setEdit(false);
        }
      }}
    >
      <SheetContent side={side} className="max-h-full overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            {baseError && (
              <div className="bg-red-100 text-sm border border-red-400 text-red-700 px-4 py-3 rounded relative w-9/12" role="alert">
                <ul className="list-disc pl-5">
                  {baseError.split("&&").map((error, index) => (
                    <li key={index}>{error.trim()}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex items-center justify-between mt-6">
              <span>
                {title ||
                  ((data as any)?.id && (data as any)?.name
                    ? `Record ID: ${(data as any)?.id}, Name: ${(data as any)?.name}`
                    : (data as any)?.id
                    ? `Record ID: ${(data as any)?.id}`
                    : (data as any)?.name) || "Create"}
              </span>
              
              
              <div className="flex justify-end mt-4">
                {edit && getAccess(["unfreeze_sow"]) && showSwitch && (
                  <div className="inline-flex items-center justify-center mr-4">
                    <label className="mr-2 font-semibold text-gray-700">
                      Unfreeze Fields:
                    </label>
                    <Switch
                      checked={unfreezeSow}
                      onCheckedChange={handleUnfreezeSow}
                    />
                  </div>
                )}
                {mode === "edit" && (
                  <Button
                    type="button"
                    onClick={() => {
                      if (edit) form.reset(data);
                      setEdit(!edit);
                      onUnfreezeSowChange(false);
                      setUnfreezeSow(false);
                    }}
                  >
                    {edit ? (
                      <>
                        <X size={16} className="mr-2" /> Cancel
                      </>
                    ) : (
                      <>
                        <SquarePen size={16} className="mr-2" /> Edit
                      </>
                    )}
                  </Button>
                )}
              </div>
              

              
            </div>
            
          </SheetTitle>
        </SheetHeader>
        <div className="mt-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(Submit)} className={formClass}>
              {Object.entries(
                formFields.reduce<Record<string, FormFieldItem<T>[]>>(
                  (acc, formField) => {
                    const group = formField.group
                      ? formField.group.toString()
                      : "default";
                    if (!acc[group]) {
                      acc[group] = [];
                    }
                    acc[group].push(formField);
                    return acc;
                  },
                  {}
                )
              ).map(([group, fields]) => (
                <div
                  key={group}
                  className={` ${
                    group !== "default" ? "flex space-x-3" : "space-y-4"
                  }`}
                >
                  {fields.map((formField) => (
                    <FormField
                      key={formField.key}
                      control={form.control}
                      name={formField.key}
                      render={({ field }) => {
                        const renderedField = formField.render(field, data);
                        
                        if (renderedField.props && renderedField.props.className && renderedField.props.className.includes("donotrender")) {
                          return <></>;
                        }
                        return (
                          <FormItem className="flex flex-col">
                            <FormLabel>
                              {formField.label}
                              {formField.required && "*"}
                            </FormLabel>
                            <FormControl>
                              {renderedField}
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        );
                      }}
                    />
                  ))}
                </div>
              ))}
              {(edit || mode === "create") && (
                <Button type="submit" disabled={loading}>Submit</Button>
              )}
            </form>
          </Form>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default TabSheet;
